import React, { useRef, useState } from 'react'
import { defaultTo, equals, find, path, pipe, prop, propOr } from 'ramda'
import PropTypes from 'prop-types'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import { getCancelToken, useDeepCompareEffect, useRequest } from 'storfox-api-hooks'

import FieldWrapper from './FieldWrapper'

const defaultGetOptionValue = value => {
  if (value) {
    const { id, guid, name } = value
    return { id, guid, name }
  }

  return null
}

const defaultGetOptionLabel = value => {
  if (value) {
    return propOr('', 'name', value)
  }

  return ''
}

const getItemFromResults = (results, id, primaryKey) =>
  find(
    pipe(
      prop(primaryKey),
      equals(id)
    )
  )(results)

function ScannerSearchField (props) {
  const {
    api,
    params,
    disabled,
    input,
    meta,
    onChange,
    renderOption,
    getOptionValue,
    getOptionLabel,
    disableClearable,
    PaperComponent,
    ListboxComponent,
    InputProps,
    onInputChange,
    onEnter,
    enterEventBlocked,
    primaryKey,
    ListboxProps,
    ...defaultProps
  } = props

  const request = useRequest()
  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const ref = useRef([])
  const enterPressed = useRef(false)

  const handleChange = event => {
    if (ref.current.length) {
      const newValue = ref.current.find(item => item.locationId === event.target.value)
      input.onChange(newValue)
      onChange(newValue)
      setOpen(false)
      setLoading(false)
    }
  }
  useDeepCompareEffect(() => {
    let active = true

    const { token, cancel } = getCancelToken()

    if (open) {
      setLoading(true)
      onInputChange(value)
      request.get(api, { ...params, search: value }, { cancelToken: token })
        .then(response => {
          const results = defaultTo([], path(['data', 'results'], response))

          if (active) {
            ref.current = results
            setOptions(results.map(getOptionValue))
            setLoading(false)
          }
        })
        .then(() => {
          if (enterPressed.current) {
            handleChange()
            enterPressed.current = false
          }
        })
    }

    return () => {
      active = false
      cancel()
    }
  }, [open, value])

  React.useEffect(() => {
    if (!open) {
      ref.current = []
      setOptions([])
    }
  }, [open])

  return (
    <Autocomplete
      open={open}
      disabled={disabled}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      disableClearable={true}
      clearOnBlur={false}
      value={input.value}
      isOptionEqualToValue={(option, value) => option[primaryKey] === value[primaryKey]}
      getOptionLabel={getOptionLabel}
      inputValue={value}
      options={options.length === 0 && !open ? [input.value] : options}
      loading={loading}
      filterOptions={item => item}
      ListboxProps={ListboxProps}
      onKeyPress={(event) => {
        if (event.key === 'Enter' && loading) {
          enterPressed.current = true
        }

        if (event.key === 'Enter' && !loading) {
          handleChange(event)
        }
      }}
      onChange={(event, value) => {
        if (value) {
          const item = getItemFromResults(ref.current, value[primaryKey], primaryKey)
          onChange(event, item)
          input.onChange(item)
        } else {
          onChange(event, null)
          input.onChange('')
        }
      }}
      PaperComponent={PaperComponent}
      ListboxComponent={ListboxComponent}
      renderOption={renderOption}
      onInputChange={(event, value) => setValue(value)}
      renderInput={params => {
        return (
          <TextField
            error={meta.invalid}
            helperText={meta.submitError || meta.error}
            {...params}
            {...defaultProps}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )
      }}
    />
  )
}

ScannerSearchField.propTypes = {
  primaryKey: PropTypes.string,
  api: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  size: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  input: PropTypes.object.isRequired,
  renderOption: PropTypes.func,
  onEnter: PropTypes.func,
  meta: PropTypes.object.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  disableClearable: PropTypes.bool.isRequired,
  InputProps: PropTypes.object,
  enterEventBlocked: PropTypes.bool,
  PaperComponent: PropTypes.func,
  ListboxComponent: PropTypes.func,
  ListboxProps: PropTypes.object
}

ScannerSearchField.defaultProps = {
  primaryKey: 'id',
  params: { limit: 30 },
  size: 'small',
  fullWidth: true,
  variant: 'outlined',
  disabled: false,
  enterEventBlocked: true,
  getOptionValue: defaultGetOptionValue,
  getOptionLabel: defaultGetOptionLabel,
  disableClearable: false,
  onChange: () => {},
  onInputChange: () => {},
  onEnter: () => {},
}

export default FieldWrapper(ScannerSearchField)
